import React, {CSSProperties, useEffect, useState} from 'react';
import {Sparklines, SparklinesBars, SparklinesLine} from 'react-sparklines';
import {isNumeric} from "utils/utils";
import {Tooltip} from "primereact/tooltip";
import { formatTime} from "utils/time-utils";
import {Dialog} from "primereact/dialog";
import DatasetLineChart from "../charts/DatasetLineChart";

import {TimeDiff} from "../../basic/timer/TimeDiff";
import {useGetRecentData} from "api/api-hooks/datasets-hooks";
import {InstantDoubleValue} from "api/model/dataset/InstantDoubleValue";

export type SparkLineProps = {
    valueScaling?: number,
    decimalPlaces?: number,
    labelFontSize?: string,
    sparkLineWidth: number,
    dataset: string | number,
    count?: number,
    unit?: string,
    showMinMax?: boolean,
    showCurrent?: boolean,
    updateFrequency?: number
}

export default function SparkLine(props: SparkLineProps) {
    const [lastValueTime, setLastValueTime] = useState<Date>();

    const [dialog, setDialog] = useState(false);
    const [dataArray, setDataArray] = useState([])

    const valueScaling = props.valueScaling || 1;
    const decimalPlaces = props.decimalPlaces || 0;
    const labelFontSize = props.labelFontSize || "2rem";
    const sparklineWidth = props.sparkLineWidth || "300px";
    const count = props.count || 14;
    const dataset = isNumeric(props.dataset) ? `${props.dataset}:CATEGORIZED_DATASET` : props.dataset as string;
    const showMinMax = props.showMinMax || false;
    const showCurrent = props.showCurrent || false;

    const {data, isLoading} = useGetRecentData([dataset],
        {
            count: count,
            options: {
                refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0
            }
        })

    useEffect(() => {
        let tempDataArr = []
        if (data && data.length != 0) {
                let ob = data[0];
                if(showCurrent && ob.data.length > 1){
                    let currentTime = new Date().getTime();
                    const diffBetweenTwoDatePoints = new Date(ob.data[1].key).getTime() - new Date(ob.data[0].key).getTime();
                    const lastGoodValueTime = new Date(ob.data[ob.data.length - 1].key).getTime();
                    const pointsBetween =  Math.floor((currentTime - lastGoodValueTime)/diffBetweenTwoDatePoints);
                    const overlappingDates = pointsBetween <= count;
                    const pointsDifference = count - pointsBetween;

                    if(!overlappingDates){
                        tempDataArr = ob.data.map((_: InstantDoubleValue) => 0)
                    } else {
                        if(ob.data.length < count) {
                            tempDataArr.length = ob.data.length;
                            tempDataArr.fill(0);
                            for (let i = 0; i < ob.data.length; i++) {
                                tempDataArr[i] = ob.data[i].value;
                            }
                        } else {
                            tempDataArr.length = count;
                            tempDataArr.fill(0);
                            for (let i = 0; i < pointsDifference; i++) {
                                tempDataArr[i] = ob.data[i].value;
                            }
                        }
                    }
                } else {
                    tempDataArr = ob.data.map((val: InstantDoubleValue) => val.value)
                }

                if (valueScaling !== 1) {
                    tempDataArr = tempDataArr.map((val: number) => val * valueScaling);
                }

                if (ob.data && ob.data.length != 0) {
                    setLastValueTime(new Date(ob.data[ob.data.length - 1].key as string));
                }
            }
        setDataArray(tempDataArr)
    }, [data]);

    const style = {fontSize: labelFontSize, fontWeight: 800, whiteSpace: "nowrap", lineHeight: "2rem"} as CSSProperties;
    const id = "sparkline_value_" + (Math.random() + 1).toString(16).substring(8);

    let el;
    if (isLoading) {
        el = <i className="pi pi-spin pi-spinner" style={{'fontSize': '4em'}}/>
    } else {
        if (dataArray && dataArray.length !== 0) {
            const valuePre = dataArray[dataArray.length - 1];
            const value = roundValue(valuePre, decimalPlaces)

            const minValue = roundValue(Math.min(...dataArray), decimalPlaces);
            const maxValue = roundValue(Math.max(...dataArray), decimalPlaces);


            el = (<>
                <div style={{lineHeight: "2rem", width: sparklineWidth, height: "40px", display: "inline-block"}}>
                    <Sparklines
                        data={dataArray}
                        height={30}
                        preserveAspectRatio={"none"}
                    >
                        <SparklinesLine style={{stroke: "#41c3f9", fill: "none"}}/>
                        <SparklinesBars style={{fill: "#41c3f9", fillOpacity: ".25"}}/>
                    </Sparklines>
                </div>
                <div id={`${id}`} className="ea-sparkline-value" style={style}>
                    {value} {props.unit}
                </div>

                <Tooltip target={`#${id}`} mouseTrack mouseTrackLeft={10}>
                    <div><b>Last value
                        time:</b> {formatTime(lastValueTime)} (age: <TimeDiff time={lastValueTime!}/>)
                    </div>
                    {showMinMax &&
                        <>
                            <div><b>Min:</b> {minValue} {props.unit}</div>
                            <div><b>Max:</b> {maxValue} {props.unit}</div>
                        </>
                    }

                </Tooltip>
            </>);
        } else {
            el = <h4>No data</h4>
        }
    }

    return <>
        <div style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            paddingBottom: "1rem",
            columnGap: "2rem"
        }}
             onDoubleClick={() => setDialog(true)}
        >
            {el}
        </div>

        <Dialog header="SCADA tags chart"
                visible={dialog}
                style={{width: 'min(100vw, 1000px)', height: "500px"}}
                modal={false}
                onHide={() => {
                    setDialog(false);
                }}
        >

            <DatasetLineChart identifiers={[dataset]}
                              showDateSelection={true}
                              dynamicHeight={true}
                              chartDialogOpen={dialog}
            />

        </Dialog>
    </>

}

const roundValue = (value: number, decimalPlaces: number = 2) => {
    return decimalPlaces === 0 ? Math.round(value) : value.toFixed(decimalPlaces);
}